// src/i18n/es/help.ts

export const help = {
  tooltip: 'Ayuda sobre esta página',
  keyFeatures: 'Funcionalidades Clave',
  quickTips: 'Consejos Rápidos',
  
  // Dashboard
  dashboard: {
    title: 'Panel de Control',
    description: 'Vista general de la actividad de la instalación con métricas en tiempo real, gráficos y análisis de journeys.',
    features: {
      kpis: 'KPIs en tiempo real: Total, tasa de completado, walking guests y duración media',
      filters: 'Filtros de fecha flexibles: hoy, ayer, 7 días, 30 días o rango personalizado',
      insights: 'Journey Insights: tiempo ahorrado, journeys digitales y canal más usado',
      charts: 'Gráficos interactivos: distribución por canal y tendencias diarias',
    },
    tips: {
      compareData: 'Usa los filtros de fecha para comparar períodos y detectar tendencias',
      quickFilters: 'Los Quick Filters te llevan directamente a journeys filtrados',
      recentActivity: 'Haz clic en cualquier journey reciente para ver sus detalles completos',
    },
  },
  
  // Activity Monitor
  activityMonitor: {
    title: 'Monitor de Actividad',
    description: 'Seguimiento en tiempo real del estado de dispositivos y sesiones activas en tu instalación.',
    features: {
      realtime: 'Estado de dispositivos en tiempo real: Online, Offline o Idle',
      deviceStatus: 'Última actividad y eventos recientes de cada dispositivo',
      activeSessions: 'Journeys en progreso en este momento con detalles',
    },
    tips: {
      offline: 'Un dispositivo Offline por más de 1 hora requiere investigación inmediata',
      abandoned: 'Sesiones abandonadas pueden indicar problemas de UX en ese dispositivo',
      autoRefresh: 'Los datos se actualizan automáticamente cada 30 segundos',
    },
  },
  
  // Journeys
  journeys: {
    title: 'Historial de Journeys',
    description: 'Búsqueda y análisis detallado de todos los check-ins con filtros avanzados y timeline visual.',
    features: {
      search: 'Búsqueda avanzada por número de reserva, habitación o huésped',
      filters: 'Filtros combinables: fecha, estado, instalación, walking guests',
      timeline: 'Timeline visual expandible para ver el flujo completo de cada journey',
      export: 'Exportación a CSV/Excel para análisis externos',
    },
    tips: {
      search: 'Usa la búsqueda rápida para encontrar reservas específicas al instante',
      combine: 'Combina filtros para análisis precisos (ej: Walking guests + Últimos 7 días)',
      timeline: 'Haz clic en la flecha ">" para expandir el timeline completo del journey',
    },
  },
  
  // Devices
  devices: {
    title: 'Gestión de Dispositivos',
    description: 'Administra kioscos, dispensadores, tabletas y aplicaciones móviles conectadas al sistema.',
    features: {
      inventory: 'Inventario completo de dispositivos con estado y última actividad',
      configuration: 'Configuración y parámetros específicos de cada dispositivo',
      monitoring: 'Estado de cada dispositivo: Activo, Inactivo o Pendiente de activación, y su última actividad',
    },
    tips: {
      naming: 'Use nombres descriptivos (p. ej.: «Kiosco recepción principal», «Dispensador planta 2»)',
      location: 'Indique la ubicación física para encontrar los dispositivos fácilmente',
      offline: 'Revise con regularidad los dispositivos sin actividad reciente para detectar problemas',
    },
  },
  
  // Users
  users: {
    title: 'Gestión de Usuarios',
    description: 'Administra usuarios, roles, permisos y accesos a instalaciones del sistema.',
    features: {
      roles: 'Roles disponibles: Administrador de Grupo (sus cadenas), Administrador de Instalación (sus instalaciones), Recepcionista (solo Recepción: llegadas, reservas y tablero en vivo) y Visor (consulta de sus instalaciones)',
      permissions: 'Permisos granulares por rol con control preciso de accesos',
      multiHotel: 'Asignación multi-instalación: usuarios pueden gestionar varias instalaciones',
    },
    tips: {
      leastPrivilege: 'Principio de mínimo privilegio: da solo los permisos necesarios',
      inactive: 'Desactivar retira solo el acceso a Insight: la cuenta y el acceso a otras aplicaciones no cambian, y Activar lo devuelve tal como estaba',
      superAdmin: 'Reserve el rol de Super Administrador para la administración del sistema, no para la operación diaria',
    },
  },
  
  // Settings
  settings: {
    title: 'Configuración',
    description: 'Parámetros del sistema, integraciones, notificaciones y preferencias personales.',
    features: {
      general: 'Configuración general: timezone, idioma, formato de fechas',
      notifications: 'Notificaciones configurables por email, SMS o push',
      integrations: 'Integraciones con sistemas de reservas, sistemas de llaves y otros servicios',
    },
    tips: {
      timezone: 'El timezone debe coincidir con la ubicación física de la instalación',
      alerts: 'Configura alertas solo para eventos críticos para evitar fatiga',
      save: 'Guarda cambios antes de salir de cada sección',
    },
  },
  
  // Alerts
  alerts: {
    title: 'Sistema de Alertas',
    description: 'El sistema genera alertas automáticamente cada 10 minutos revisando el estado de los dispositivos y la frecuencia de errores críticos.',
    features: {
      deviceOffline: 'Dispositivo sin conexión (Warning): se genera cuando un dispositivo no envía eventos durante 35 minutos (intervalo de heartbeat de 30 min + 5 min de margen). Solo se dispara una vez por cada transición de activo a inactivo.',
      highErrorRate: 'Alta tasa de errores críticos (Critical): se genera cuando un dispositivo acumula 10 o más errores de severidad Crítica en 1 hora. Cooldown de 4 horas entre alertas del mismo dispositivo.',
      dispenserErrors: 'Errores críticos en dispensador (Critical): se genera cuando un dispensador acumula 3 o más errores de severidad Crítica en 1 hora. Cooldown de 4 horas entre alertas del mismo dispensador.',
    },
    tips: {
      severityVsNature: 'Las alertas se basan en la Severidad del error (Crítico/Advertencia/Info), no en su Naturaleza (Error/Operacional/Informativo). Un evento operacional con severidad Crítica (p.ej. hopper vacío) sí genera alerta.',
      channels: 'Las alertas se envían por los canales configurados en cada instalación: email, SMS o WhatsApp. Revise la configuración de la instalación para verificar los destinatarios.',
      cooldown: 'Tras enviar una alerta, el sistema espera 4 horas antes de generar otra del mismo tipo para el mismo dispositivo, evitando alertas repetitivas.',
      days: 'Las fechas son días del hotel: cada día va de la hora de cierre del hotel (las 04:00 si no tiene otra) a la misma hora del día siguiente, en su zona horaria.',
    },
  },
  
  // Check-ins
  checkIns: {
    title: 'Historial de Check-ins',
    description: 'Vista detallada de todos los check-ins realizados en el sistema.',
    features: {
      filters: 'Filtros avanzados por fecha, instalación, estado y canal',
      details: 'Información completa de cada check-in con timeline de eventos',
      export: 'Exportación para análisis y reportes externos',
    },
    tips: {
      status: 'Los check-ins "Complete" incluyen pre-check-in y entrega de llave',
      search: 'Busca por número de reserva para encontrar check-ins específicos',
      timeline: 'Expande el timeline para ver el flujo completo del proceso',
    },
  },
  
  // Groups
  groups: {
    title: 'Gestión de Grupos',
    description: 'Administra grupos de instalaciones y sus configuraciones globales.',
    features: {
      hierarchy: 'Jerarquía organizacional: Grupos > Instalaciones > Dispositivos',
      settings: 'Configuración global heredada por todas las instalaciones del grupo',
      permissions: 'Control de acceso por grupo para los administradores de grupo',
    },
    tips: {
      naming: 'Usa nombres descriptivos que reflejen la estructura organizacional',
      inheritance: 'Las configuraciones del grupo se heredan a las instalaciones',
      consolidation: 'Agrupa instalaciones por región o marca para mejor gestión',
    },
  },
  
  // Hotels
  hotels: {
    title: 'Gestión de Instalaciones',
    description: 'Administra instalaciones individuales, configuraciones y asignaciones de usuarios.',
    features: {
      configuration: 'Configuración específica por instalación: timezone, idioma, moneda',
      devices: 'Gestión de dispositivos asignados a la instalación',
      users: 'Usuarios con acceso a la instalación y sus permisos',
    },
    tips: {
      active: 'Marca instalaciones como inactivas temporalmente sin perder configuración',
      timezone: 'El timezone de la instalación debe coincidir con su ubicación física',
      branding: 'Personaliza colores y logos para mantener la identidad de marca',
    },
  },
  
  // Error Monitor
  errorMonitor: {
    title: 'Monitor de Errores',
    description: 'Análisis detallado de errores y abandonos del sistema.',
    features: {
      tracking: 'Seguimiento de errores por tipo, dispositivo y frecuencia',
      abandonment: 'Análisis de abandonos con identificación de puntos críticos',
      trends: 'Tendencias de errores para identificar problemas sistemáticos',
    },
    tips: {
      focus: 'Prioriza errores que afectan múltiples dispositivos',
      context: 'Revisa el contexto completo del error antes de escalar',
      prevention: 'Usa el análisis de patrones para prevención proactiva',
      days: 'Las fechas son días del hotel: cada día va de la hora de cierre del hotel (las 04:00 si no tiene otra) a la misma hora del día siguiente, en su zona horaria.',
    },
  },
};

export default help;
